import type { UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Camera, Clock3, UserRound, UsersRound, X } from 'lucide-react';
import type { CleaningTaskDetailResponse, CleaningTaskQueueResponse } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { BottomSheet, Button } from '@/shared/components';
import { arrivalLabel, cleaningDayStatus } from './cleaningDayModel';
import { CleaningPreparationSummary } from '../CleaningPreparationSummary';
import { cleaningOccupancySummary } from '../cleaningOccupancy';
import { windowLabel } from '../my-tasks/taskLabels';
import { TaskRoomsSection } from './TaskRoomsSection';

interface Props {
  task: CleaningTaskQueueResponse | null;
  detail: UseQueryResult<CleaningTaskDetailResponse>;
  date: string;
  desktop: boolean;
  onOpenChange: (open: boolean) => void;
  onClaim: (task: CleaningTaskQueueResponse) => void;
  claimingId?: string;
  offline: boolean;
}

/** Blatt einer Aufgabe aus Liste oder Woche: Fenster, Person, Fotos, nächste Anreise, ein Weg. */
export function CleaningTaskDetailPanel(props: Props) {
  if (props.desktop) {
    return props.task ? (
      <aside
        aria-label={`Details zu ${props.task.propertyName}`}
        className="border-hairline rounded-tile sticky top-5 max-h-[calc(var(--shell-viewport,100dvh)-2.5rem)] overflow-y-auto border bg-white"
      >
        <DetailContent {...props} />
      </aside>
    ) : null;
  }
  return (
    <BottomSheet
      open={props.task !== null}
      onOpenChange={props.onOpenChange}
      title={`Details zu ${props.task?.propertyName ?? ''}`}
      description="Reinigungsstand, Fotos und nächste Anreise"
      desktop="panel"
      className="max-h-[92dvh] bg-white"
      bare
    >
      <DetailContent {...props} />
    </BottomSheet>
  );
}

function DetailContent({ task, detail, date, onOpenChange, onClaim, claimingId, offline }: Props) {
  const { user } = useAuth();
  if (!task) return null;
  const arrival = task.nextArrival;
  return (
    <>
      <header className="border-hairline relative border-b px-5 py-5 md:px-6">
        <button
          type="button"
          aria-label="Schließen"
          onClick={() => onOpenChange(false)}
          className="text-ink-soft hover:text-ink rounded-pill absolute right-3 top-3 flex size-11 items-center justify-center"
        >
          <X size={19} />
        </button>
        <h2 className="pr-10 font-serif text-2xl font-bold">{task.propertyName}</h2>
        {task.propertyInternalName && task.propertyInternalName !== task.propertyName && (
          <p className="text-ink-soft mt-1 text-sm">Intern: {task.propertyInternalName}</p>
        )}
        <p className="text-forest mt-2 text-sm font-semibold">
          {cleaningDayStatus(task, user?.userId)}
        </p>
      </header>
      <div className="flex flex-col gap-5 px-5 py-5 md:px-6">
        <section className="flex flex-col gap-3">
          <Fact icon={Clock3} label="Fenster" value={windowLabel(task)} />
          <Fact
            icon={UserRound}
            label="Zuständig"
            value={
              task.assignedToUserName ?? (task.assignedToUserId ? 'Zuweisung prüfen' : 'Noch frei')
            }
          />
          <Fact
            icon={Camera}
            label="Fotos"
            value={
              task.photoSlotCount > 0
                ? `${task.filledPhotoSlotCount} von ${task.photoSlotCount}`
                : 'ohne Fotos'
            }
          />
        </section>
        <section className="border-hairline flex flex-col gap-3 border-t pt-4">
          <h3 className="font-serif text-lg font-bold">Nächste Anreise</h3>
          <p className="text-amber-ink text-sm font-semibold">{arrivalLabel(task, date)}</p>
          <CleaningPreparationSummary arrival={arrival} />
          {arrival && (
            <Fact icon={UsersRound} label="Belegung" value={cleaningOccupancySummary(arrival)} />
          )}
        </section>
        {task.canViewDetails && <TaskRoomsSection detail={detail} />}
        {task.canClaim ? (
          <Button
            variant="brand"
            size="lg"
            className="w-full"
            loading={claimingId === task.id}
            disabled={offline}
            onClick={() => onClaim(task)}
          >
            Übernehmen &amp; starten
          </Button>
        ) : (
          <Link
            to="/cleaning/$id"
            params={{ id: task.id }}
            className="rounded-pill bg-forest flex min-h-12 w-full items-center justify-center px-4 text-sm font-bold text-white"
          >
            {task.canViewDetails ? 'Aufgabe öffnen' : 'Zusammenfassung öffnen'}
          </Link>
        )}
        {!task.canViewDetails && (
          <p className="text-ink-soft -mt-2 text-xs">
            {task.canClaim
              ? 'Räume und Notiz siehst du nach dem Übernehmen.'
              : 'Räume und Notiz sehen nur die zuständige Person und die Leitung.'}
          </p>
        )}
      </div>
    </>
  );
}

function Fact({ icon: Icon, label, value }: { icon: typeof Clock3; label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <Icon className="text-forest mt-0.5 shrink-0" size={18} aria-hidden="true" />
      <div>
        <p className="text-ink-faint text-[11px] font-bold uppercase tracking-wide">{label}</p>
        <p className="mt-0.5 text-sm">{value}</p>
      </div>
    </div>
  );
}
